/* CUSTOM — collection page behaviour.

   Two independent enhancements, both of which the page works without:

   - The styling matrix links a plotted point to the products either side of it,
     so hovering one dot dims the rest. Without this the dots are plain links.
   - The routine builder keeps a running total and posts the whole routine to the
     cart in one request. Without this the form still submits normally and the
     browser lands on /cart. */

(() => {
  'use strict';

  /* Three sections on Shop All pull this file in — the hero, the styling matrix
     and the routine builder — and a browser runs every <script src> it is given,
     identical URLs included. Without this the routine form's submit handler
     bound three times and one click added three of everything. */
  if (window.mgCollectionInitialised) return;
  window.mgCollectionInitialised = true;

  /* ── Jump links ──────────────────────────────────────────────────────────
     The hero's chips are ordinary fragment links and work without this. They
     are driven explicitly anyway, for two reasons: native fragment navigation
     was landing about 310px short of the target here even though the sections
     don't move and `scroll-margin-top` is applied correctly — `scrollIntoView`
     lands exactly right — and taking the click over means focus has to be moved
     by hand, which the browser would otherwise have done for us. */

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const jumpTo = (target) => {
    // The offset comes from the element's own scroll-margin-top, so the CSS
    // stays the one place the sticky header's height is accounted for and the
    // no-JavaScript path lands in exactly the same spot.
    const offset = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    const top = Math.max(0, Math.round(target.getBoundingClientRect().top + window.scrollY - offset));
    const before = Math.round(window.scrollY);

    // Position is worked out here rather than left to scrollIntoView: the
    // browser's own idea of where a section starts disagreed with the box it
    // reports by ~310px on this page, and a rect we measured can't disagree
    // with itself.
    // A smooth scroll is driven by animation frames, and a hidden document does
    // not run them — the scroll would simply never start and the page would sit
    // where it was. Same trap as the accordion's height animation; see the note
    // in CLAUDE.md. Jump instantly there, as we do for reduced motion.
    const instant = reduceMotion.matches || document.hidden;
    window.scrollTo({ top, behavior: instant ? 'auto' : 'smooth' });

    return { top, before };
  };

  document.querySelectorAll('.mg-hero__jump a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (event) => {
      const hash = link.getAttribute('href');
      const target = document.querySelector(hash);
      // No target: leave it to the browser rather than swallowing the click.
      if (!target) return;

      event.preventDefault();

      // Native fragment navigation moves focus as well as the viewport, so a
      // keyboard or screen reader user carries on from the target rather than
      // from the link they just left. preventScroll stops it jumping on its
      // own — the scroll below is the one that should happen.
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });

      jumpTo(target);
      history.replaceState(null, '', hash);
    });
  });

  /* ── Styling matrix ──────────────────────────────────────────────────────── */

  document.querySelectorAll('.mg-matrix__points').forEach((points) => {
    const focusOn = (handle) => {
      points.classList.toggle('mg-matrix__points--focused', handle !== null);
      points.querySelectorAll('[data-mg-point]').forEach((point) => {
        point.classList.toggle('is-active', point.dataset.mgPoint === handle);
      });
    };

    points.addEventListener('pointerover', (event) => {
      const point = event.target.closest('[data-mg-point]');
      if (point) focusOn(point.dataset.mgPoint);
    });

    points.addEventListener('pointerleave', () => focusOn(null));
    points.addEventListener('focusin', (event) => {
      const point = event.target.closest('[data-mg-point]');
      if (point) focusOn(point.dataset.mgPoint);
    });
    points.addEventListener('focusout', (event) => {
      // Only clear once focus has actually left the plot, not while moving
      // between two dots inside it.
      if (!points.contains(event.relatedTarget)) focusOn(null);
    });
  });

  /* Dawn's cart notification keys its product markup off one line item —
     `renderContents` reads `parsedState.key` — and a multi-item add returns no
     such key, so it looked for `cart-notification-product-undefined`, found
     nothing and threw on innerHTML. That is why adding the Care Duo or a whole
     routine never popped the notification.

     The section itself already renders every line in the cart, each under its
     own id, so the data was always there. Hand Dawn the first key to run its
     own plumbing, then widen the markup to every line that was added. */
  const showCartNotification = (cart, body) => {
    const added = Array.isArray(body.items) ? body.items : [body];
    cart.renderContents({ ...body, key: body.key ?? added[0]?.key });

    if (added.length < 2) return;
    const source = body.sections?.['cart-notification-product'];
    const target = document.getElementById('cart-notification-product');
    if (!source || !target) return;

    const rendered = new DOMParser().parseFromString(source, 'text/html');
    target.innerHTML = added
      .map((item) => rendered.querySelector(`[id="cart-notification-product-${item.key}"]`)?.outerHTML ?? '')
      .join('');
  };

  /* ── Routine builder ─────────────────────────────────────────────────────── */

  document.querySelectorAll('[data-mg-routine]').forEach((form) => {
    const totalEl = form.querySelector('[data-mg-routine-total]');
    const errorEl = form.querySelector('[data-mg-routine-error]');
    const submit = form.querySelector('[type="submit"]');
    const currency = form.dataset.mgCurrency || 'GBP';

    const money = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency,
    });

    const chosen = () => [...form.querySelectorAll('.mg-routine__radio:checked')];

    const totalRow = form.querySelector('.mg-routine__total');
    const wasEl = form.querySelector('[data-mg-routine-was]');
    const pillEl = form.querySelector('[data-mg-routine-pill]');
    const percentOff = Number(totalRow?.dataset.mgDiscountPercent) || 0;
    const fixedOff = (Number(totalRow?.dataset.mgDiscountFixed) || 0) * 100;

    const renderTotal = () => {
      if (!totalEl) return;
      const pence = chosen().reduce((sum, input) => sum + Number(input.dataset.mgPrice || 0), 0);

      /* Both come off the original subtotal and are summed — Regios does not
         compound them, so this mirrors what the cart will actually charge
         rather than applying one after the other. */
      const saving = Math.min(pence, Math.round(pence * (percentOff / 100)) + fixedOff);
      const discounted = pence - saving;
      const showSaving = saving > 0 && pence > 0;

      totalEl.textContent = money.format((showSaving ? discounted : pence) / 100);
      if (wasEl) {
        wasEl.textContent = showSaving ? money.format(pence / 100) : '';
        wasEl.hidden = !showSaving;
      }
      if (pillEl) pillEl.hidden = !showSaving;
    };

    /* Every step must have something chosen before the routine can be added.
       In practice that means the styling step, which is the only one that can
       be emptied — its boxes are checkboxes, so a customer can untick the lot,
       and adding a wash and conditioner alone is not the routine the section is
       selling and would not qualify for the discount either. */
    const steps = [...form.querySelectorAll('.mg-routine__step')];
    const everyStepChosen = () => steps.every((step) => step.querySelector('.mg-routine__radio:checked'));

    const syncSubmitState = () => {
      if (!submit) return;
      const ready = everyStepChosen();
      submit.disabled = !ready;
      submit.setAttribute('aria-disabled', String(!ready));
    };

    const showError = (message) => {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = !message;
    };

    form.addEventListener('change', () => {
      renderTotal();
      syncSubmitState();
    });
    renderTotal();
    syncSubmitState();

    form.addEventListener('submit', async (event) => {
      // fetchConfig and routes come from Dawn's global.js. If either is missing
      // the native form post is still a working add-to-cart, so bail out rather
      // than break it.
      if (typeof fetchConfig !== 'function' || typeof routes === 'undefined') return;

      event.preventDefault();
      showError('');

      const items = chosen().map((input) => ({ id: Number(input.value), quantity: 1 }));
      if (items.length === 0 || !everyStepChosen()) return;

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const config = fetchConfig('javascript');
      config.body = JSON.stringify({
        items,
        sections: cart ? cart.getSectionsToRender().map((section) => section.id) : [],
        sections_url: window.location.pathname,
      });

      submit?.classList.add('loading');
      submit?.setAttribute('aria-disabled', 'true');
      cart?.setActiveElement(document.activeElement);

      try {
        const response = await fetch(routes.cart_add_url, config);
        const body = await response.json();

        if (body.status) {
          showError(body.description || body.message);
          return;
        }

        if (!cart) {
          window.location = routes.cart_url;
          return;
        }

        cart.classList.remove('is-empty');

        try {
          showCartNotification(cart, body);
        } catch (renderError) {
          /* The add succeeded; a notification that won't render must not be
             reported as a failure. Fall back to keeping the count honest. */
          console.warn('Cart notification could not render.', renderError);
          try {
            const markup = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`);
            const parsed = new DOMParser().parseFromString(await markup.text(), 'text/html');
            const fresh = parsed.querySelector('.shopify-section');
            const current = document.querySelector('#cart-icon-bubble');
            if (fresh && current) current.innerHTML = fresh.innerHTML;
          } catch (bubbleError) {
            console.warn('Could not refresh the cart count.', bubbleError);
          }
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mg-routine-builder', cartData: body });
        }
      } catch (error) {
        console.error(error);
        showError('Something went wrong adding that. Please try again.');
      } finally {
        submit?.classList.remove('loading');
        submit?.removeAttribute('aria-disabled');
      }
    });
  });
})();
